// components/ReviewDeadlineWidget.tsx
import { Box, Flex } from '@chakra-ui/core'
import React from 'react'
import { WidgetWrapper } from 'components/TasksV2/UI/WidgetWrapper'
import useLocaleText from 'components/useLocaleText'
import { Controller, useFormContext } from 'react-hook-form'
import { DeadlinePickerComponent } from '../DeadlineWidget/DeadlinePickerComponent'
import { DateToLocaleValue } from 'components/ui/DateToLocaleValue'
import useReviewDeadlinePicker from './useReviewDeadlinePicker'
import dayjs from 'dayjs'
import { FormInputLabel } from 'components/TasksV2/UI/FormInputLabel'
import CustomTimePicker from 'components/CommonComponents/CutomTimePicker'
import { ErrorMessage } from 'components/ui/Input/ErrorMessage'
import { X } from 'lucide-react'

const ReviewDeadlineWidget: React.FC = () => {
  const { control, errors, watch } = useFormContext()
  const t_review_end_date = useLocaleText('t_review_end_date')
  const t_review_to_ts_error = useLocaleText('t_review_to_ts_error')
  const {
    reviewToTs,
    isRecurring,
    effectiveToTs,
    reviewTime,
    isDatePickerOpen,
    handleDateChange,
    handleTimeChange,
    clearReviewDate,
    openDatePicker,
    closeDatePicker,
  } = useReviewDeadlinePicker()

  return (
    <Box pos="relative">
      <FormInputLabel label={t_review_end_date} />
      <Flex alignItems="center" style={{ columnGap: '15px' }}>
        <WidgetWrapper
          p="10px"
          isError={!!errors?.review_to_ts}
          cursor="pointer"
          onClick={openDatePicker}
          flexDir="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <DateToLocaleValue
            date={reviewToTs}
            placeholder={t_review_end_date}
            format="DD MMMM YYYY"
            minW="180px"
            textAlign="center"
          />
        </WidgetWrapper>
        <WidgetWrapper display={isRecurring ? 'none' : 'flex'} isError={!!errors?.review_to_ts}>
          <CustomTimePicker value={reviewTime} onChange={handleTimeChange} />
        </WidgetWrapper>
        {reviewToTs && (
          <Box onClick={clearReviewDate} cursor="pointer" role="button" aria-label="clear review end date">
            <X size={18} />
          </Box>
        )}
      </Flex>

      <Controller
        name="review_to_ts"
        control={control}
        key="review_to_ts"
        rules={{
          validate: (value) => {
            // Optional field — empty is always valid.
            if (!value) {
              return undefined
            }

            const reviewDate = dayjs(value)

            if (!reviewDate.isValid()) {
              return t_review_to_ts_error
            }

            const deadline = watch('deadline')
            const allowedOverdueDays = watch('allowed_overdue_days')
            const effectiveTo = dayjs(deadline).add(Number(allowedOverdueDays || 0), 'day')

            if (reviewDate.isBefore(effectiveTo)) {
              return t_review_to_ts_error
            }
            return undefined
          },
        }}
        as={
          <DeadlinePickerComponent
            isOpen={isDatePickerOpen}
            onClose={closeDatePicker}
            selectedDate={reviewToTs}
            handleChange={handleDateChange}
            minDate={effectiveToTs.toDate()}
          />
        }
      />
      {errors?.review_to_ts && (
        <Box px="10px" pt="5px">
          <ErrorMessage errorMessage={t_review_to_ts_error} />
        </Box>
      )}
    </Box>
  )
}

export default ReviewDeadlineWidget
